﻿import { hasDataPage, qs } from "../core/dom";
import { highlightCode, preloadShiki } from "../lib/shiki-highlighter";

let outputRenderVersion = 0;
let inputRenderVersion = 0;

export async function initJsonToCSharpPage() {
    if (!hasDataPage("json-to-csharp")) return;

    const page = {
        jsonInput: qs("[data-json-csharp-input]"),
        jsonHighlight: qs("[data-json-csharp-highlight]"),
        output: qs("[data-json-csharp-output]"),
        status: qs("[data-json-csharp-status]"),

        namespaceInput: qs("[data-json-csharp-namespace]"),
        rootClassInput: qs("[data-json-csharp-root-class]"),
        typeStyle: qs("[data-json-csharp-type-style]"),
        arrayType: qs("[data-json-csharp-array-type]"),

        pascalCase: qs("[data-json-csharp-pascal-case]"),
        nullable: qs("[data-json-csharp-nullable]"),
        required: qs("[data-json-csharp-required]"),
        initCollections: qs("[data-json-csharp-init-collections]"),

        attributeProvider: qs("[data-json-csharp-attribute-provider]"),
        includeAttributes: qs("[data-json-csharp-include-attributes]"),
        includeUsings: qs("[data-json-csharp-include-usings]"),
        includeHeader: qs("[data-json-csharp-header]"),

        generateBtn: qs("[data-json-csharp-generate]"),
        loadExampleBtn: qs("[data-json-csharp-load-example]"),
        clearBtn: qs("[data-json-csharp-clear]"),
        copyBtn: qs("[data-json-csharp-copy]"),
        downloadBtn: qs("[data-json-csharp-download]")
    };

    bindEvents(page);

    await preloadShiki();
    loadExample(page);
}

function bindEvents(page) {
    page.jsonInput?.addEventListener("input", debounce(async () => {
        await renderJsonInput(page);
        generateCSharp(page);
    }, 150));

    page.jsonInput?.addEventListener("scroll", () => syncInputScroll(page));

    const settings = [
        page.namespaceInput,
        page.rootClassInput,
        page.typeStyle,
        page.arrayType,
        page.pascalCase,
        page.nullable,
        page.required,
        page.initCollections,
        page.attributeProvider,
        page.includeAttributes,
        page.includeUsings,
        page.includeHeader
    ].filter(Boolean);

    settings.forEach(item => {
        item.addEventListener("input", () => generateCSharp(page));
        item.addEventListener("change", () => generateCSharp(page));
    });

    page.generateBtn?.addEventListener("click", () => generateCSharp(page));
    page.loadExampleBtn?.addEventListener("click", () => loadExample(page));
    page.clearBtn?.addEventListener("click", async () => clearAll(page));
    page.copyBtn?.addEventListener("click", async () => copyOutput(page));
    page.downloadBtn?.addEventListener("click", () => downloadOutput(page));

    document.addEventListener("click", (e) => {
        const toggle = e.target.closest("[data-theme-toggle]");
        if (!toggle) return;

        setTimeout(async () => {
            await renderJsonInput(page);
            await renderCSharpOutput(page, page.output?.dataset.rawCode || "");
        }, 80);
    });
}

async function loadExample(page) {
    if (!page.jsonInput) return;

    page.jsonInput.value = JSON.stringify({
        id: 1001,
        reference: "ORD-2026-001",
        createdAt: "2026-05-12T10:25:00Z",
        isPaid: true,
        customer: {
            id: "7f7bdcf2-5d4f-4f97-a06e-6f6798f72e1a",
            full_name: "John Doe",
            email: "john@example.com",
            phone: "+905551112233"
        },
        items: [
            {
                product_id: 10,
                productName: "Premium QR Package",
                quantity: 2,
                unitPrice: 49.90,
                tags: ["digital", "qr", "premium"]
            }
        ],
        metadata: {
            source: "website",
            campaign: null
        }
    }, null, 2);

    await renderJsonInput(page);
    generateCSharp(page);
}

async function clearAll(page) {
    if (page.jsonInput) page.jsonInput.value = "";
    if (page.output) {
        page.output.innerHTML = "";
        page.output.dataset.rawCode = "";
    }

    await renderJsonInput(page);
    setStatus(page, "Ready", "");
}

async function renderJsonInput(page) {
    if (!page.jsonInput || !page.jsonHighlight) return;

    const version = ++inputRenderVersion;
    const code = page.jsonInput.value || " ";

    const html = await highlightCode(code, "json");

    if (version !== inputRenderVersion) return;

    page.jsonHighlight.innerHTML = html;
    syncInputScroll(page);
}

function syncInputScroll(page) {
    if (!page.jsonInput || !page.jsonHighlight) return;

    page.jsonHighlight.scrollTop = page.jsonInput.scrollTop;
    page.jsonHighlight.scrollLeft = page.jsonInput.scrollLeft;
}

function generateCSharp(page) {
    try {
        const raw = page.jsonInput?.value?.trim();

        if (!raw) {
            renderCSharpOutput(page, "");
            setStatus(page, "Empty", "");
            return;
        }

        const parsed = JSON.parse(raw);
        const options = readOptions(page);
        const code = generateCode(parsed, options);

        renderCSharpOutput(page, code);
        setStatus(page, "Generated", "is-valid");
    } catch (error) {
        const message = error instanceof Error ? error.message : "Invalid JSON.";
        const code = `// Invalid JSON\n// ${message}`;
        renderCSharpOutput(page, code);
        setStatus(page, "Invalid JSON", "is-error");
    }
}

function readOptions(page) {
    return {
        namespaceName: sanitizeNamespace(page.namespaceInput?.value || "MyApp.Models"),
        rootClassName: sanitizeClassName(page.rootClassInput?.value || "RootModel"),
        typeStyle: page.typeStyle?.value || "class",
        arrayType: page.arrayType?.value || "list",
        pascalCase: !!page.pascalCase?.checked,
        nullable: !!page.nullable?.checked,
        required: !!page.required?.checked,
        initCollections: !!page.initCollections?.checked,
        attributeProvider: page.attributeProvider?.value || "none",
        includeAttributes: !!page.includeAttributes?.checked,
        includeUsings: !!page.includeUsings?.checked,
        includeHeader: !!page.includeHeader?.checked
    };
}

function generateCode(json, options) {
    const context = {
        classes: new Map(),
        order: [],
        options
    };

    const rootType = inferType(json, options.rootClassName, context);

    if (rootType.kind === "array") {
        const itemType = rootType.itemType;
        if (itemType.kind === "object") {
            // root array içindeki object zaten class olarak üretilir
        }
    }

    const parts = [];

    if (options.includeHeader) {
        parts.push("// <auto-generated>");
        parts.push("// Generated by JSON to C# Entity tool.");
        parts.push("// </auto-generated>");
        parts.push("");
    }

    if (options.nullable) {
        parts.push("#nullable enable");
        parts.push("");
    }

    if (options.includeUsings) {
        const usings = buildUsings(options);
        if (usings.length) {
            usings.forEach(u => parts.push(`using ${u};`));
            parts.push("");
        }
    }

    if (options.namespaceName) {
        parts.push(`namespace ${options.namespaceName};`);
        parts.push("");
    }

    const orderedClasses = context.order
        .map(name => context.classes.get(name))
        .filter(Boolean);

    for (let i = 0; i < orderedClasses.length; i++) {
        parts.push(renderClass(orderedClasses[i], options));

        if (i < orderedClasses.length - 1) {
            parts.push("");
        }
    }

    return parts.join("\n");
}

function buildUsings(options) {
    const usings = new Set();

    if (options.arrayType === "list") {
        usings.add("System.Collections.Generic");
    }

    if (options.arrayType === "ienumerable") {
        usings.add("System.Collections.Generic");
    }

    if (options.includeAttributes && options.attributeProvider === "system-text-json") {
        usings.add("System.Text.Json.Serialization");
    }

    if (options.includeAttributes && options.attributeProvider === "newtonsoft") {
        usings.add("Newtonsoft.Json");
    }

    return Array.from(usings);
}

function inferType(value, suggestedName, context) {
    if (value === null || value === undefined) {
        return { kind: "null", name: "object", nullable: true };
    }

    if (Array.isArray(value)) {
        return inferArrayType(value, suggestedName, context);
    }

    const type = typeof value;

    if (type === "boolean") {
        return { kind: "primitive", name: "bool", nullable: false };
    }

    if (type === "number") {
        return { kind: "primitive", name: inferNumberType(value), nullable: false };
    }

    if (type === "string") {
        return { kind: "primitive", name: inferStringType(value), nullable: false, reference: true };
    }

    if (type === "object") {
        return inferObjectType(value, suggestedName, context);
    }

    return { kind: "primitive", name: "object", nullable: true, reference: true };
}

function inferArrayType(array, suggestedName, context) {
    if (!array.length) {
        return {
            kind: "array",
            itemType: { kind: "primitive", name: "object", nullable: true, reference: true },
            nullable: false
        };
    }

    const itemTypes = array.map(item => inferType(item, singularize(suggestedName), context));
    const merged = mergeTypes(itemTypes, singularize(suggestedName), context);

    return {
        kind: "array",
        itemType: merged,
        nullable: false
    };
}

function inferObjectType(obj, suggestedName, context) {
    const className = sanitizeClassName(suggestedName || "GeneratedModel");

    if (!context.classes.has(className)) {
        const model = {
            name: className,
            properties: []
        };

        context.classes.set(className, model);
        context.order.push(className);

        const entries = Object.entries(obj);

        for (const [jsonName, value] of entries) {
            const propertyName = sanitizePropertyName(jsonName, context.options.pascalCase);
            const childClassName = sanitizeClassName(propertyName);
            const type = inferType(value, childClassName, context);

            model.properties.push({
                jsonName,
                propertyName,
                type,
                isRequired: value !== null && value !== undefined
            });
        }
    }

    return {
        kind: "object",
        name: className,
        nullable: false,
        reference: true
    };
}

function mergeTypes(types, suggestedName, context) {
    const nonNullTypes = types.filter(x => x.kind !== "null");
    const hasNull = nonNullTypes.length !== types.length;

    if (!nonNullTypes.length) {
        return { kind: "primitive", name: "object", nullable: true, reference: true };
    }

    const first = nonNullTypes[0];

    if (nonNullTypes.every(t => sameType(t, first))) {
        return {
            ...first,
            nullable: hasNull || first.nullable
        };
    }

    const objectTypes = nonNullTypes.filter(t => t.kind === "object");
    if (objectTypes.length === nonNullTypes.length) {
        return {
            ...objectTypes[0],
            nullable: hasNull
        };
    }

    const numberTypes = nonNullTypes.filter(t => t.kind === "primitive" && isNumericType(t.name));
    if (numberTypes.length === nonNullTypes.length) {
        const names = numberTypes.map(x => x.name);
        const name = names.includes("decimal") ? "decimal" :
            names.includes("double") ? "double" :
                names.includes("long") ? "long" :
                    "int";

        return { kind: "primitive", name, nullable: hasNull };
    }

    return { kind: "primitive", name: "object", nullable: hasNull, reference: true };
}

function sameType(a, b) {
    if (a.kind !== b.kind) return false;

    if (a.kind === "array") {
        return sameType(a.itemType, b.itemType);
    }

    return a.name === b.name;
}

function renderClass(model, options) {
    const lines = [];

    const keyword = options.typeStyle === "class"
        ? "class"
        : options.typeStyle === "record"
            ? "record"
            : "record class";

    lines.push(`public ${keyword} ${model.name}`);
    lines.push("{");

    for (const property of model.properties) {
        const propertyLines = renderProperty(property, options);
        propertyLines.forEach(line => lines.push(`    ${line}`));
        lines.push("");
    }

    if (lines[lines.length - 1] === "") {
        lines.pop();
    }

    lines.push("}");
    return lines.join("\n");
}

function renderProperty(property, options) {
    const lines = [];

    if (options.includeAttributes && options.attributeProvider !== "none") {
        if (options.attributeProvider === "system-text-json") {
            lines.push(`[JsonPropertyName("${escapeCSharpString(property.jsonName)}")]`);
        }

        if (options.attributeProvider === "newtonsoft") {
            lines.push(`[JsonProperty("${escapeCSharpString(property.jsonName)}")]`);
        }
    }

    const typeName = renderType(property.type, options, property.isRequired);
    const requiredKeyword = shouldUseRequired(property, options) ? "required " : "";
    const initializer = getInitializer(property.type, options);

    lines.push(`public ${requiredKeyword}${typeName} ${property.propertyName} { get; set; }${initializer}`);

    return lines;
}

function renderType(type, options, isRequired) {
    if (type.kind === "array") {
        const item = renderType(type.itemType, options, true).replace(/\?$/, "");

        if (options.arrayType === "array") {
            return `${item}[]${type.nullable && options.nullable ? "?" : ""}`;
        }

        if (options.arrayType === "ienumerable") {
            return `IEnumerable<${item}>${type.nullable && options.nullable ? "?" : ""}`;
        }

        return `List<${item}>${type.nullable && options.nullable ? "?" : ""}`;
    }

    const isNullable = options.nullable && shouldBeNullable(type, isRequired);

    if (type.name === "string") {
        return `string${isNullable ? "?" : ""}`;
    }

    if (isValueType(type.name)) {
        return `${type.name}${isNullable ? "?" : ""}`;
    }

    return `${type.name}${isNullable ? "?" : ""}`;
}

function shouldBeNullable(type, isRequired) {
    if (type.nullable) return true;
    if (type.reference && !isRequired) return true;
    return false;
}

function shouldUseRequired(property, options) {
    if (!options.required) return false;
    if (!options.nullable) return false;
    if (!property.isRequired) return false;

    const type = property.type;

    if (type.kind === "array") return true;
    if (type.kind === "object") return true;
    if (type.name === "string") return true;

    return false;
}

function getInitializer(type, options) {
    if (!options.initCollections) return "";

    if (type.kind === "array") {
        if (options.arrayType === "list") return " = new();";
        if (options.arrayType === "array") return " = Array.Empty<object>();";
    }

    return "";
}

function inferNumberType(value) {
    if (Number.isInteger(value)) {
        if (value >= -2147483648 && value <= 2147483647) return "int";
        return "long";
    }

    const text = String(value);
    if (text.includes(".") && text.split(".")[1]?.length <= 6) {
        return "decimal";
    }

    return "double";
}

function inferStringType(value) {
    if (isGuid(value)) return "Guid";
    if (isDateTime(value)) return "DateTime";
    return "string";
}

function isGuid(value) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function isDateTime(value) {
    if (typeof value !== "string") return false;
    if (!/\d{4}-\d{2}-\d{2}/.test(value)) return false;

    const date = Date.parse(value);
    return !Number.isNaN(date);
}

function isValueType(name) {
    return [
        "int", "long", "short", "byte",
        "decimal", "double", "float",
        "bool", "DateTime", "Guid"
    ].includes(name);
}

function isNumericType(name) {
    return ["int", "long", "decimal", "double", "float"].includes(name);
}

function sanitizeNamespace(value) {
    return String(value || "")
        .split(".")
        .map(part => sanitizeClassName(part))
        .filter(Boolean)
        .join(".");
}

function sanitizeClassName(value) {
    const name = toPascalCase(value || "GeneratedModel");
    return /^[0-9]/.test(name) ? `Model${name}` : name;
}

function sanitizePropertyName(value, pascalCase) {
    const raw = pascalCase ? toPascalCase(value) : String(value || "Property");
    const clean = raw.replace(/[^a-zA-Z0-9_]/g, "");

    const name = clean || "Property";
    const fixed = /^[0-9]/.test(name) ? `Property${name}` : name;

    if (CSharpKeywords.has(fixed)) {
        return `@${fixed}`;
    }

    return fixed;
}

function toPascalCase(value) {
    return String(value || "")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .split(/[^a-zA-Z0-9]+|_+/)
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join("") || "GeneratedModel";
}

function singularize(value) {
    const name = String(value || "Item");
    if (name.endsWith("ies")) return name.slice(0, -3) + "y";
    if (name.endsWith("ses")) return name.slice(0, -2);
    if (name.endsWith("s") && name.length > 1) return name.slice(0, -1);
    return `${name}Item`;
}

function escapeCSharpString(value) {
    return String(value)
        .replaceAll("\\", "\\\\")
        .replaceAll('"', '\\"');
}

async function renderCSharpOutput(page, code) {
    if (!page.output) return;

    const currentVersion = ++outputRenderVersion;
    const html = await highlightCode(code || " ", "csharp");

    if (currentVersion !== outputRenderVersion) return;

    page.output.dataset.rawCode = code || "";
    page.output.innerHTML = html;
}

function setStatus(page, text, stateClass = "") {
    if (!page.status) return;

    page.status.textContent = text;
    page.status.classList.remove("is-valid", "is-error");

    if (stateClass) {
        page.status.classList.add(stateClass);
    }
}

async function copyOutput(page) {
    const code = page.output?.dataset.rawCode || "";
    await navigator.clipboard.writeText(code);
    setStatus(page, "Copied", "is-valid");
}

function downloadOutput(page) {
    const code = page.output?.dataset.rawCode || "";
    const rootName = sanitizeClassName(page.rootClassInput?.value || "RootModel");

    const blob = new Blob([code], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${rootName}.cs`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setTimeout(() => URL.revokeObjectURL(url), 500);
}

const CSharpKeywords = new Set([
    "abstract", "as", "base", "bool", "break", "byte", "case", "catch",
    "char", "checked", "class", "const", "continue", "decimal", "default",
    "delegate", "do", "double", "else", "enum", "event", "explicit",
    "extern", "false", "finally", "fixed", "float", "for", "foreach",
    "goto", "if", "implicit", "in", "int", "interface", "internal", "is",
    "lock", "long", "namespace", "new", "null", "object", "operator",
    "out", "override", "params", "private", "protected", "public",
    "readonly", "record", "ref", "return", "sbyte", "sealed", "short",
    "sizeof", "stackalloc", "static", "string", "struct", "switch",
    "this", "throw", "true", "try", "typeof", "uint", "ulong",
    "unchecked", "unsafe", "ushort", "using", "virtual", "void",
    "volatile", "while"
]);

function debounce(fn, delay = 150) {
    let timer = null;

    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
    };
}